/**
 * Bulk Upload CSV schema — template generation, parsing, validation & posting.
 */

import { EQUIPMENT_FLAT_LIST } from '@/lib/equipmentTypes';
import { supabase } from '@/integrations/supabase/client';
import { generateLoadNumber } from '@/lib/generateLoadNumber';

/* ------------------------------------------------------------------ */
/*  Column definitions                                                 */
/* ------------------------------------------------------------------ */

export const BULK_CSV_HEADERS = [
  'pickup_date',
  'origin_city',
  'origin_state',
  'destination_city',
  'destination_state',
  'equipment_type',
  'load_type',
  'weight_lbs',
  'length_ft',
  'rate_amount',
  'commodity',
  'notes',
  'reference_id',
] as const;

const REQUIRED_FIELDS: ReadonlySet<string> = new Set([
  'pickup_date',
  'origin_city',
  'origin_state',
  'destination_city',
  'destination_state',
  'equipment_type',
  'load_type',
  'weight_lbs',
  'length_ft',
  'rate_amount',
]);

/* ------------------------------------------------------------------ */
/*  Load-type normalisation                                            */
/* ------------------------------------------------------------------ */

/** Internal values accepted by the posting model */
const VALID_LOAD_TYPES = ['FTL', 'LTL', 'contract'] as const;

/** Map friendly CSV aliases → canonical internal value */
const LOAD_TYPE_ALIASES: Record<string, string> = {
  ftl: 'FTL',
  full: 'FTL',
  'full load': 'FTL',
  'full truckload': 'FTL',
  ltl: 'LTL',
  partial: 'LTL',
  contract: 'contract',
};

function normalizeLoadType(raw: string): string | null {
  const key = raw.trim().toLowerCase();
  return LOAD_TYPE_ALIASES[key] ?? null;
}

/* ------------------------------------------------------------------ */
/*  Equipment-type normalisation                                       */
/* ------------------------------------------------------------------ */

/** Build a lookup from lowercase name/code/enum → enumValue */
const equipLookup: Record<string, string> = {};
for (const item of EQUIPMENT_FLAT_LIST) {
  equipLookup[item.name.toLowerCase()] = item.enumValue;
  equipLookup[item.code.toLowerCase()] = item.enumValue;
  equipLookup[item.enumValue.toLowerCase()] = item.enumValue;
}

function normalizeEquipmentType(raw: string): string | null {
  const key = raw.trim().toLowerCase();
  return equipLookup[key] ?? null;
}

/* ------------------------------------------------------------------ */
/*  Template generation                                                */
/* ------------------------------------------------------------------ */

const HINT_ROW = [
  '# YYYY-MM-DD',
  'City name',
  '2-letter state',
  'City name',
  '2-letter state',
  'Van/Flatbed/Reefer/StepDeck…',
  'FTL/LTL/contract',
  'Positive number',
  'Positive number',
  'Positive number',
  '(optional)',
  '(optional)',
  '(optional)',
];

const EXAMPLE_ROW = [
  '2026-04-01',
  'Dallas',
  'TX',
  'Chicago',
  'IL',
  'Van',
  'FTL',
  '42000',
  '53',
  '2850',
  'Electronics',
  'Fragile',
  'REF123',
];

export function generateTemplateCSV(): string {
  const header = BULK_CSV_HEADERS.join(',');
  const hints = HINT_ROW.join(',');
  const example = EXAMPLE_ROW.join(',');
  return `${header}\n${hints}\n${example}\n`;
}

export function downloadTemplateCSV() {
  const csv = generateTemplateCSV();
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = 'LaneRunner_Bulk_Load_Template.csv';
  a.click();
  URL.revokeObjectURL(url);
}

/* ------------------------------------------------------------------ */
/*  Parsing                                                            */
/* ------------------------------------------------------------------ */

export interface BulkRow {
  rowNumber: number;
  data: Record<string, string>;
  /** Normalised values ready for posting (set only on valid rows) */
  normalised?: {
    load_type: string;
    equipment_type: string;
  };
}

export interface BulkRowError {
  row: BulkRow;
  errors: string[];
}

export interface BulkParseResult {
  validRows: BulkRow[];
  invalidRows: BulkRowError[];
  totalRows: number;
}

function parseLine(line: string): string[] {
  // Simple CSV split — handles quoted fields with commas
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current.trim());
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current.trim());
  return result;
}

/* ------------------------------------------------------------------ */
/*  Validation                                                         */
/* ------------------------------------------------------------------ */

const DATE_RE = /^\d{4}-\d{2}-\d{2}$/;

export function validateRow(row: BulkRow): string[] {
  const errs: string[] = [];
  const d = row.data;

  // Required fields presence
  for (const field of REQUIRED_FIELDS) {
    if (!d[field] || d[field].trim() === '') {
      errs.push(`Missing required field: ${field}`);
    }
  }
  if (errs.length > 0) return errs; // stop early if required fields missing

  // Date format
  if (!DATE_RE.test(d.pickup_date)) {
    errs.push('pickup_date must be YYYY-MM-DD');
  }

  // Numeric checks
  for (const field of ['weight_lbs', 'length_ft', 'rate_amount'] as const) {
    const num = Number(d[field]);
    if (isNaN(num) || num <= 0) {
      errs.push(`${field} must be a positive number`);
    }
  }

  // Load type
  const normLoad = normalizeLoadType(d.load_type);
  if (!normLoad) {
    errs.push(`Invalid load_type "${d.load_type}" — use ${VALID_LOAD_TYPES.join(', ')}`);
  }

  // Equipment type
  const normEquip = normalizeEquipmentType(d.equipment_type);
  if (!normEquip) {
    errs.push(`Unrecognised equipment_type "${d.equipment_type}"`);
  }

  // Attach normalised values when valid
  if (errs.length === 0 && normLoad && normEquip) {
    row.normalised = { load_type: normLoad, equipment_type: normEquip };
  }

  return errs;
}

/* ------------------------------------------------------------------ */
/*  Geocoding helper                                                   */
/* ------------------------------------------------------------------ */

interface GeocodedCity {
  city: string;
  state: string;
  lat: number;
  lng: number;
}

/**
 * Resolve a city+state pair to coordinates using the local search_locations
 * RPC (30 k-city DB → geocode_cache → Mapbox fallback).
 */
export async function geocodeCity(city: string, state: string): Promise<GeocodedCity | null> {
  const query = `${city}, ${state}`;

  // 1. Try local DB search
  const { data } = await supabase.rpc('search_locations', { q: query, lim: 1 });
  if (data && data.length > 0) {
    const hit = data[0] as { city: string; state_code: string; lat: number; lon: number };
    if (hit.lat != null && hit.lon != null) {
      return { city: hit.city, state: hit.state_code, lat: hit.lat, lng: hit.lon };
    }
  }

  // 2. Mapbox fallback via edge function
  const { data: geoData, error } = await supabase.functions.invoke('geocoding', {
    body: { query },
  });
  if (error || !geoData?.features?.length) return null;

  const feature = geoData.features[0];
  const [lng, lat] = feature.center ?? feature.geometry?.coordinates ?? [];
  if (lat == null || lng == null) return null;

  // Cache the result for next time
  supabase.functions.invoke('geocode-cache-upsert', {
    body: {
      query,
      place_name: feature.place_name ?? query,
      city,
      state_code: state,
      lat,
      lon: lng,
      mapbox_place_id: feature.id ?? null,
    },
  }).catch(() => {}); // fire-and-forget

  return { city, state, lat, lng };
}

/* ------------------------------------------------------------------ */
/*  Payload builder                                                    */
/* ------------------------------------------------------------------ */

export interface BulkLoadPayload {
  origin_name: string;
  origin_address: string;
  origin_city: string;
  origin_state: string;
  origin_country: string;
  origin_postal_code: string | null;
  origin_latitude: number;
  origin_longitude: number;
  destination_name: string;
  destination_address: string;
  destination_city: string;
  destination_state: string;
  destination_country: string;
  destination_postal_code: string | null;
  destination_latitude: number;
  destination_longitude: number;
  pickup_date: string;
  delivery_date: string;
  equipment_type: string;
  weight_lbs: number;
  length_ft: number;
  load_type: string;
  rate_amount: number;
  rate_type: string;
  rate_currency: string;
  commodity: string | null;
  special_instructions: string | null;
  reference_number: string | null;
  company_id: string;
  created_by: string;
  status: string;
  load_number: string;
}

/**
 * Build a load insert payload from a validated BulkRow + geocoded locations.
 */
export function buildLoadPayload(
  row: BulkRow,
  origin: GeocodedCity,
  destination: GeocodedCity,
  companyId: string,
  userId: string,
): BulkLoadPayload {
  const d = row.data;
  const norm = row.normalised!;
  return {
    origin_name: origin.city,
    origin_address: `${origin.city}, ${origin.state}`,
    origin_city: origin.city,
    origin_state: origin.state,
    origin_country: 'US',
    origin_postal_code: null,
    origin_latitude: origin.lat,
    origin_longitude: origin.lng,
    destination_name: destination.city,
    destination_address: `${destination.city}, ${destination.state}`,
    destination_city: destination.city,
    destination_state: destination.state,
    destination_country: 'US',
    destination_postal_code: null,
    destination_latitude: destination.lat,
    destination_longitude: destination.lng,
    pickup_date: d.pickup_date,
    delivery_date: d.pickup_date, // default to pickup
    equipment_type: norm.equipment_type,
    weight_lbs: Number(d.weight_lbs),
    length_ft: Number(d.length_ft),
    load_type: norm.load_type,
    rate_amount: Number(d.rate_amount),
    rate_type: 'spot',
    rate_currency: 'USD',
    commodity: d.commodity?.trim() || null,
    special_instructions: d.notes?.trim() || null,
    reference_number: d.reference_id?.trim() || null,
    company_id: companyId,
    created_by: userId,
    status: 'posted',
    load_number: `${generateLoadNumber()}-${row.rowNumber}`,
  };
}

/* ------------------------------------------------------------------ */
/*  Public: parse + validate                                           */
/* ------------------------------------------------------------------ */

export function parseAndValidateCSV(text: string): BulkParseResult {
  const lines = text.split(/\r?\n/).filter((l) => l.trim() !== '');
  if (lines.length === 0) return { validRows: [], invalidRows: [], totalRows: 0 };

  const headerLine = lines[0];
  const headers = parseLine(headerLine).map((h) => h.toLowerCase().replace(/\s+/g, '_'));

  const validRows: BulkRow[] = [];
  const invalidRows: BulkRowError[] = [];

  for (let i = 1; i < lines.length; i++) {
    if (lines[i].trimStart().startsWith('#')) continue;
    const values = parseLine(lines[i]);
    const data: Record<string, string> = {};
    headers.forEach((h, idx) => {
      data[h] = values[idx] ?? '';
    });

    const row: BulkRow = { rowNumber: i + 1, data };
    const errors = validateRow(row);

    if (errors.length > 0) {
      invalidRows.push({ row, errors });
    } else {
      validRows.push(row);
    }
  }

  return { validRows, invalidRows, totalRows: validRows.length + invalidRows.length };
}
